/* eslint-disable react/jsx-props-no-spreading */
import { Backdrop, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { UPDropdown, UPDropdownItem } from 'components/dropdown';
import UPInput from 'components/input';
import CATEGORIES from 'constants/Categories';
import { toNumber } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import { addFunctionAction, updateFunctionAction } from 'store/actions/company-function-actions';
import {
  getCategoriesDescriptionsAction,
  getParitairComiteSelectItemsByCompanyAction,
} from 'store/actions/paritair-comite-actions';
import { paritairComiteByCompanySelectItemsSelector } from 'store/selectors/paritair-comite-selectors';
import theme from 'theme';
import IPayrollFunction from 'types/company/payroll/IPayrollFunction';
import { getInputErrorText } from 'utils/helpers';
import { ReactComponent as CloseIcon } from '../../../assets/icons/Close.svg';
import {
  COMPANY_PAYROLL__ADD_NEW_FUNCTION,
  COMPANY_PAYROLL__CATEGORY,
  COMPANY_PAYROLL__EDIT_PAYROLL_FUNCTION,
  COMPANY_PAYROLL__FUNCTION_DESCRIPTION,
  COMPANY_PAYROLL__PARITAIR_COMITE,
  EMPLOYEES__LABOURER_FULL,
  EMPLOYEES__SERVANT_FULL,
  GENERAL__CANCEL_BUTTON,
  GENERAL__SAVE_BUTTON,
  GENERAL__SELECT,
  VALIDATION__IS_REQUIRED,
} from '../../../translations/constants';
import {
  PayrollEditFunctionModal,
  PayrollEditFunctionModalActionButton,
  PayrollEditFunctionModalActionContainer,
  PayrollEditFunctionModalCloseIcon,
  PayrollEditFunctionModalColumn,
  PayrollEditFunctionModalContainer,
  PayrollEditFunctionModalInputBox,
  PayrollEditFunctionModalInputContainer,
  PayrollEditFunctionModalTitle,
} from './styles';
import { EditPayrollFunctionProps } from './types';

const EditPayrollFunction = (props: EditPayrollFunctionProps): JSX.Element => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId } = useParams();
  const { open, onClose, payrollFunction } = props;
  const [descriptions, setDescriptions] = useState<{ category: string; description: string }[]>([]);

  const categories = useMemo((): UPDropdownItem[] => {
    return CATEGORIES.map(category => {
      const matchingDescription = descriptions.find(desc => desc.category === category.label);
      return {
        ...category,
        label: matchingDescription?.description
          ? `${category.label}\u00A0\u00A0\u00A0(${matchingDescription.description})`
          : category.label,
      };
    });
  }, [descriptions]);

  const paritairComites = useAppSelector(paritairComiteByCompanySelectItemsSelector);

  const {
    handleSubmit,
    control,
    getValues,
    watch,
    formState: { isDirty, errors },
  } = useForm<IPayrollFunction>({
    defaultValues: payrollFunction,
  });

  useEffect(() => {
    dispatch(getParitairComiteSelectItemsByCompanyAction(toNumber(companyId)));
  }, [companyId, dispatch]);

  const onSavePayrollFunction: SubmitHandler<IPayrollFunction> = event => {
    const values = getValues();
    if (isDirty) {
      if (payrollFunction.id === 0) {
        dispatch(addFunctionAction({ companyId: toNumber(companyId), functionToAdd: values }));
      } else {
        dispatch(updateFunctionAction({ companyId: toNumber(companyId), functionToUpdate: values }));
      }
    }
    onClose(event);
  };

  const watchCollarType = watch('class');
  const watchParitairComite = watch('paritairComiteId');
  useEffect(() => {
    const fetchDescriptions = async () => {
      try {
        const fetchedDescriptions = await dispatch(
          getCategoriesDescriptionsAction({ paritairComiteId: `${watchParitairComite}`, collarType: watchCollarType }),
        ).unwrap();
        setDescriptions(fetchedDescriptions);
      } catch (error) {
        console.error('An unexpected error occurred:', error);
      }
    };
    if (watchCollarType && watchParitairComite) {
      fetchDescriptions();
    }
  }, [dispatch, watchCollarType, watchParitairComite]);

  return (
    <Backdrop open={open} sx={{ zIndex: '1200' }}>
      <PayrollEditFunctionModal open={open} onClose={onClose} aria-labelledby="modal-edit-payroll-function">
        <PayrollEditFunctionModalContainer>
          <PayrollEditFunctionModalTitle>
            {payrollFunction.id === 0
              ? t(COMPANY_PAYROLL__ADD_NEW_FUNCTION)
              : t(COMPANY_PAYROLL__EDIT_PAYROLL_FUNCTION)}
          </PayrollEditFunctionModalTitle>
          <PayrollEditFunctionModalCloseIcon onClick={onClose}>
            <CloseIcon fill={theme.palette.secondary.contrastText} />
          </PayrollEditFunctionModalCloseIcon>
          <PayrollEditFunctionModalInputContainer>
            <PayrollEditFunctionModalColumn sx={{ marginRight: '16px' }}>
              <PayrollEditFunctionModalInputBox>
                <Controller
                  name="description"
                  control={control}
                  rules={{ required: true, maxLength: 100 }}
                  render={({ field }) => (
                    <UPInput
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t(COMPANY_PAYROLL__FUNCTION_DESCRIPTION)}
                      label={t(COMPANY_PAYROLL__FUNCTION_DESCRIPTION)}
                      required
                      error={!!errors.description}
                      helperText={getInputErrorText(errors, 'description', undefined, 100)}
                    />
                  )}
                />
              </PayrollEditFunctionModalInputBox>
              <PayrollEditFunctionModalInputBox>
                <Controller
                  name="paritairComiteId"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <UPDropdown
                      items={paritairComites}
                      value={field.value.toString()}
                      onChange={field.onChange}
                      placeholder={t(GENERAL__SELECT)}
                      label={t(COMPANY_PAYROLL__PARITAIR_COMITE)}
                      required
                      error={!!errors.paritairComiteId}
                      helperText={getInputErrorText(errors, 'paritairComiteId', undefined)}
                    />
                  )}
                />
              </PayrollEditFunctionModalInputBox>
              <PayrollEditFunctionModalInputBox>
                <Controller
                  name="class"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => {
                    return (
                      <RadioGroup row {...field}>
                        <FormControlLabel value="BED" control={<Radio />} label={t(EMPLOYEES__SERVANT_FULL)} />
                        <FormControlLabel value="ARB" control={<Radio />} label={t(EMPLOYEES__LABOURER_FULL)} />
                      </RadioGroup>
                    );
                  }}
                />
                {errors.class && (
                  <Typography color="error" variant="body2">
                    {t(VALIDATION__IS_REQUIRED)}
                  </Typography>
                )}
              </PayrollEditFunctionModalInputBox>
              <PayrollEditFunctionModalInputBox>
                <Controller
                  name="category"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <UPDropdown
                      items={categories}
                      value={field.value?.toString()}
                      onChange={field.onChange}
                      placeholder={t(GENERAL__SELECT)}
                      label={t(COMPANY_PAYROLL__CATEGORY)}
                      required
                      error={!!errors.category}
                      helperText={getInputErrorText(errors, 'category', undefined)}
                    />
                  )}
                />
              </PayrollEditFunctionModalInputBox>
            </PayrollEditFunctionModalColumn>
          </PayrollEditFunctionModalInputContainer>
          <PayrollEditFunctionModalActionContainer>
            <PayrollEditFunctionModalActionButton
              text={t(GENERAL__SAVE_BUTTON)}
              onClick={event => handleSubmit(onSavePayrollFunction)(event)}
            />
            <PayrollEditFunctionModalActionButton text={t(GENERAL__CANCEL_BUTTON)} outlined onClick={onClose} />
          </PayrollEditFunctionModalActionContainer>
        </PayrollEditFunctionModalContainer>
      </PayrollEditFunctionModal>
    </Backdrop>
  );
};

export default EditPayrollFunction;
